import {useEffect, useRef, useState} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import Header from './components/Header';
import headerStyles from './components/Header/Header.module.less';
import styles from './MainLayout.module.less';
import LoginModal from '@/components/common/LoginModal';
import LogoutConfirmModal from '@/components/common/LogoutConfirmModal';
import SideBar from '@/components/common/SideBar';
import {PAGE_CONTENT_SCROLL_ID} from '@/constants';
import {HOME_PATH} from '@/router/constants';
import {useModalStore} from '@/store/useModalStore';

const MainLayout: React.FC = () => {
    const location = useLocation();
    const closePointsModal = useModalStore((s) => s.closePointsModal);
    const togglePointsRechargeModal = useModalStore((s) => s.togglePointsRechargeModal);
    const pageContentRef = useRef<HTMLDivElement>(null);
    const [scrolled, setScrolled] = useState(false);

    // 仅首页需要顶部毛玻璃悬浮效果，其余页面（如资产页）保持正常文档流
    const isHome = location.pathname === HOME_PATH;

    // 路由变化时关闭全局弹窗，防止前进/后退时弹窗残留
    useEffect(() => {
        closePointsModal();
        // 积分充值弹窗关闭时会由 PointsRechargeModal 内部 cancel 支付会话，
        // 避免路由切换后订单悬挂
        togglePointsRechargeModal(false);
    }, [closePointsModal, togglePointsRechargeModal, location.pathname]);

    useEffect(() => {
        if (!isHome) {
            setScrolled(false);
            return undefined;
        }
        const el = pageContentRef.current;
        if (!el) {
            return undefined;
        }
        const handleScroll = () => {
            setScrolled(el.scrollTop > 0);
        };
        handleScroll();
        el.addEventListener('scroll', handleScroll, {passive: true});
        // eslint-disable-next-line consistent-return
        return () => el.removeEventListener('scroll', handleScroll);
    }, [isHome]);

    // 仅首页给 Header 加 overlay/scrolled，复用 Header 内已有的毛玻璃样式
    const headerClassName = isHome
        ? [headerStyles.overlay, scrolled && headerStyles.scrolled].filter(Boolean).join(' ')
        : undefined;

    const pageContentClassName = isHome
        ? `${styles.pageContent} ${styles.pageContentHome}`
        : styles.pageContent;

    return (
        <div className={styles.layout}>
            <Header className={headerClassName} />
            <div className={styles.body}>
                <SideBar />
                <div
                    id={PAGE_CONTENT_SCROLL_ID}
                    ref={pageContentRef}
                    className={pageContentClassName}
                >
                    <div className={styles.contentContainer}>
                        <Outlet />
                    </div>
                </div>
            </div>
            <LoginModal />
            <LogoutConfirmModal />
        </div>
    );
};

export default MainLayout;
